import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { FilteredFloorActionHandlers } from "./useFloorActionHandlers"

export interface UseFloorActionsArgs
  extends Partial<FilteredFloorActionHandlers> {
  idPrefix: string
  isReadonly?: boolean
  onOpenDetails?: () => void
  isOpenDetailsDisabled?: boolean
  isEditDisabled?: boolean
  isDuplicateDisabled?: boolean
  isCopyDisabled?: boolean
  isAssignAttachmentsDisabled?: boolean
  isAddClarificationRequestDisabled?: boolean
  isDeleteDisabled?: boolean
}

export function useFloorActions({
  idPrefix,
  isReadonly,
  onOpenDetails,
  isOpenDetailsDisabled,
  onEdit,
  isEditDisabled,
  onDuplicate,
  isDuplicateDisabled,
  onCopy,
  isCopyDisabled,
  onAssignAttachments,
  isAssignAttachmentsDisabled,
  onAddClarificationRequest,
  isAddClarificationRequestDisabled,
  onDelete,
  isDeleteDisabled
}: UseFloorActionsArgs) {
  const { t } = useTranslation()

  const openDetailsAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__open-details`,
      "data-testid": "floor__actions__open-details",
      icon: IconName.ZOOM_IN,
      label: t("Floor.Actions.OpenDetails"),
      disabled: isOpenDetailsDisabled,
      onClick: onOpenDetails ?? undefined
    }),
    [t, idPrefix, isOpenDetailsDisabled, onOpenDetails]
  )

  const editAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__edit`,
      "data-testid": "floor__actions__edit",
      icon: IconName.EDIT,
      label: t("Floor.Actions.Update"),
      disabled: isEditDisabled,
      onClick: onEdit ?? undefined
    }),
    [t, idPrefix, isEditDisabled, onEdit]
  )

  const duplicateAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__duplicate`,
      "data-testid": "floor__actions__duplicate",
      icon: IconName.DUPLICATE,
      label: t("Floor.Actions.Duplicate"),
      disabled: isDuplicateDisabled,
      onClick: onDuplicate ?? undefined
    }),
    [t, idPrefix, isDuplicateDisabled, onDuplicate]
  )

  const copyAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__copy`,
      "data-testid": "floor__actions__copy",
      icon: IconName.SHARE,
      label: t("Floor.Actions.Copy"),
      disabled: isCopyDisabled,
      onClick: onCopy ?? undefined
    }),
    [t, idPrefix, isCopyDisabled, onCopy]
  )

  const assignAttachmentsAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__assign-attachments`,
      "data-testid": "floor__actions__assign-attachments",
      icon: IconName.PAPERCLIP,
      label: t("Floor.Actions.AssignAttachments"),
      disabled: isAssignAttachmentsDisabled,
      onClick: onAssignAttachments ?? undefined
    }),
    [t, idPrefix, isAssignAttachmentsDisabled, onAssignAttachments]
  )

  const addClarificationRequestAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__add-clarification-request`,
      "data-testid": "floor__actions__add-clarification-request",
      icon: IconName.CLARIFICATION_REQUEST,
      label: t("Floor.Actions.AddClarificationRequest"),
      disabled: isAddClarificationRequestDisabled,
      onClick: onAddClarificationRequest ?? undefined
    }),
    [t, idPrefix, isAddClarificationRequestDisabled, onAddClarificationRequest]
  )

  const deleteAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__floor__actions__delete`,
      "data-testid": "floor__actions__delete",
      icon: IconName.BIN,
      label: t("Floor.Actions.Delete"),
      isDanger: true,
      disabled: isDeleteDisabled,
      onClick: onDelete ?? undefined
    }),
    [isDeleteDisabled, onDelete]
  )

  const allActions = useMemo<Action[]>(
    () => [
      openDetailsAction,
      editAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction
    ],
    [
      openDetailsAction,
      editAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction
    ]
  )

  const readonlyActions = useMemo<Action[]>(() => [copyAction], [copyAction])

  const actions = useMemo<Action[]>(
    () =>
      (isReadonly ? readonlyActions : allActions).filter(
        action => !!action.onClick
      ),
    [allActions, readonlyActions, isReadonly]
  )

  return {
    actions,
    openDetailsAction,
    editAction,
    duplicateAction,
    copyAction,
    assignAttachmentsAction,
    addClarificationRequestAction,
    deleteAction
  }
}
